import { yupResolver } from '@hookform/resolvers/yup';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import type { OrderDetailsOrder } from '@/types/orderDetailsOrder';

import { SectionHeader } from '@/components/SectionHeader';
import { Default } from '@/layouts/Default';
import { captureError } from '@/lib/errors';
import { EMAIL_REGX } from '@/lib/forms/const';
import { paths } from '@/lib/paths';
import type { PaymentMethod } from '@/lib/payment/types';
import type { WithdrawalEligibility } from '@/lib/withdrawal/types';
import { formMessages } from '@/messages/forms';
import { orderMessages } from '@/messages/order';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import { GuestOrderDetailsView } from '@/views/GuestOrderDetailsView';
import { OrderReturnView } from '@/views/OrderReturnView';

import { OrderStatusForm } from './components/OrderStatusForm';

import * as S from './OrderStatusView.styles';

interface FormValues {
  captcha: boolean;
  orderEmail: string;
  orderNumber: string;
  recaptchaToken: string | null;
}

interface OrderStatusData {
  data: {
    order: OrderDetailsOrder;
  };
  paymentMethod: PaymentMethod[] | PaymentMethod;
}

type ActivePanel = 'details' | 'return';

interface GuestAuthContext {
  orderEmail: string;
  orderNumber: string;
  recaptchaToken: string;
}

export const OrderStatusView = () => {
  const [enableCaptchaBtn, setEnableCaptchaBtn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [orderStatusData, setOrderStatusData] =
    useState<OrderStatusData | null>(null);
  const [activePanel, setActivePanel] = useState<ActivePanel>('details');
  const [guestAuthContext, setGuestAuthContext] =
    useState<GuestAuthContext | null>(null);
  const [guestWithdrawalEligibility, setGuestWithdrawalEligibility] =
    useState<WithdrawalEligibility | null>(null);
  const [error, setError] = useState<null | string>(null);
  const [recaptchaKey, setRecaptchaKey] = useState(Date.now());

  const { formatMessage } = useIntl();

  const { locale } = useRegionProvider();
  const requiredMsg = formatMessage(formMessages.requiredField);

  const schema = yup.object().shape({
    orderNumber: yup.string().required(requiredMsg).default(''),
    orderEmail: yup
      .string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(''),
    captcha: yup
      .bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
    recaptchaToken: yup.string().nullable().default(null),
  });

  const formCtx = useForm<FormValues>({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });

  const captcha = formCtx.watch('captcha');
  const orderNumber = formCtx.watch('orderNumber');
  const orderEmail = formCtx.watch('orderEmail');

  const guestLookup = useMemo(() => {
    if (!guestAuthContext) {
      return null;
    }
    return {
      businessId: guestAuthContext.orderNumber,
      email: guestAuthContext.orderEmail,
    };
  }, [guestAuthContext?.orderEmail, guestAuthContext?.orderNumber]);

  useEffect(() => {
    setEnableSubmit(
      !!orderNumber && !!orderEmail && captcha && enableCaptchaBtn,
    );
  }, [orderNumber, orderEmail, captcha, enableCaptchaBtn]);

  const onRecaptchaChange = (token: string | null) => {
    setError(null);
    formCtx.setValue('recaptchaToken', token);
    formCtx.setValue('captcha', true);
    setEnableCaptchaBtn(true);
  };

  const onRecaptchaExpired = () => {
    setEnableCaptchaBtn(false);
    formCtx.setValue('captcha', false);
    formCtx.setValue('recaptchaToken', null);
    setRecaptchaKey(Date.now());
  };

  const handleSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsProcessing(true);
    setError(null);

    try {
      const req = await fetch(paths.api.order.status.asPath(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          languageCode: locale.code,
        }),
      });

      const respData = await req.json();

      if (req.ok) {
        setOrderStatusData(respData);
        if (data.recaptchaToken) {
          setGuestAuthContext({
            orderNumber: data.orderNumber,
            orderEmail: data.orderEmail,
            recaptchaToken: data.recaptchaToken,
          });
        }
        setActivePanel('details');
        return;
      }

      if (!req.ok) {
        onRecaptchaExpired();
        switch (respData.errorCode) {
          case 'NO_MATCH':
            setError(formatMessage(orderMessages.noMatch));
            break;
          case 'FETCH_ERROR':
            setError(formatMessage(orderMessages.fetchError));
            break;
          default:
            setError(respData.message);
        }
      }
    } catch (error) {
      setError(formatMessage(orderMessages.errorNetwork));
      onRecaptchaExpired();
      captureError(error as Error, {
        extra: {
          error,
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const body = (() => {
    if (!orderStatusData) {
      return (
        <OrderStatusForm
          enableSubmit={enableSubmit}
          error={error}
          formCtx={formCtx}
          handleSubmit={handleSubmit}
          isProcessing={isProcessing}
          onRecaptchaChange={onRecaptchaChange}
          onRecaptchaExpired={onRecaptchaExpired}
          recaptchaKey={recaptchaKey}
        />
      );
    }

    if (activePanel === 'details') {
      return (
        <GuestOrderDetailsView
          guestLookup={guestLookup}
          onEligibilityChange={setGuestWithdrawalEligibility}
          onStartReturn={() => setActivePanel('return')}
          order={orderStatusData.data.order}
          paymentMethod={orderStatusData.paymentMethod}
          withdrawalEligibility={guestWithdrawalEligibility}
        />
      );
    }

    return (
      <OrderReturnView
        guestAuthContext={guestAuthContext}
        mode='guest'
        onGuestCancel={() => setActivePanel('details')}
        onGuestEligibilityChange={setGuestWithdrawalEligibility}
        order={orderStatusData.data.order}
        orderId={orderStatusData.data.order.id}
        withdrawalEligibilityOverride={guestWithdrawalEligibility}
      />
    );
  })();

  return (
    <>
      <S.SectionHeaderWrapper>
        <SectionHeader
          color='black'
          colorized={true}
          sizeDesktop={50}
          sizeMobile={40}
          text={formatMessage(orderMessages.checkYourOrder)}
          textAlign='center'
        />
      </S.SectionHeaderWrapper>
      <S.OrderStatusViewBodyContainer>{body}</S.OrderStatusViewBodyContainer>
    </>
  );
};

OrderStatusView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>
  </DraftModeContext.Provider>
);
